import { observer } from 'mobx-react-lite'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useIsMounted, Translate2 } from 'common'
import { useSpecificConditionStore } from '../../../../store/specificConditions/SpecificConditionsStore'
import { breakpoints } from '../../../../theme/breakpoints'
import { Button } from '../../../../ui-components/forms/Button'
import styled from 'styled-components'
import { EditControls } from './EditControls'
import { VehicleDropdown } from './VehicleDropdown'
import UserDataServiceProvider from '../../../../services/UserDataServiceProvider'
import VehicleServiceProvider from '../../../../services/VehicleServiceProvider'
import { usePartsCatalog } from '../../../../store/partsCatalog/PartsCatalog'
import { AUTO_LIGHT_MEDIUM_TRUCK_ID } from 'results'
import { VehicleDropdownOnChangeProps } from '../interfaces'
import {
  useVehicleWidgetStore,
  VehicleWidgetDisplayState,
  VehicleWidgetErrorState,
} from '../store/VehicleWidgetStore'
import { useVehicleMakesStore } from '../store/VehicleMakesStore'
import { VehicleSelectorStep } from '../../../../ui-components/util/Interfaces'
import { StoreInstances } from '../../../../store/StoreInstancesContainer'
import { sortVehicleTypes } from '../../../../store/customiseSearch/Utils'
import { useSearchStore } from '../../../../store/search/SearchStore'

const stepOrder = [
  VehicleSelectorStep.START,
  VehicleSelectorStep.TYPE,
  VehicleSelectorStep.YEAR,
  VehicleSelectorStep.MAKE,
  VehicleSelectorStep.MODEL,
  VehicleSelectorStep.ENGINE,
  VehicleSelectorStep.END,
]

interface VehicleSelectorInterface {
  onVehicleSelected: () => void
  startStep?: VehicleSelectorStep
}

export const VehicleSelector = observer(
  ({
    onVehicleSelected,
    startStep: startStepProps,
  }: VehicleSelectorInterface): ReactElement => {
    const startStep = startStepProps || VehicleSelectorStep.TYPE
    const navigate = useNavigate()
    const { clearStore, resetStore } = usePartsCatalog()
    const isMounted = useIsMounted()
    const searchStore = useSearchStore()
    const vehicleWidgetStore = useVehicleWidgetStore()
    const makesStore = useVehicleMakesStore()
    const typeId = searchStore.currentFieldsSelections.type?.id
    const isMakesFilterEnabled = makesStore.isMakesFilterEnabled()
    const customMakesKey = makesStore.customMakesKey
    const filterMakes = useMemo(
      () => isMakesFilterEnabled && AUTO_LIGHT_MEDIUM_TRUCK_ID === typeId,
      [isMakesFilterEnabled, typeId]
    )

    const editing =
      vehicleWidgetStore.displayState === VehicleWidgetDisplayState.edit
    const [isYearAutoOpened, setIsYearAutoOpened] = useState<boolean>(false)

    const {
      currentFieldsSelections: currentSelections,
      setCurrentFieldsSelections: setCurrentSelections,
      currentVehicle,
    } = searchStore

    const [userClickedOnEngine, setUserClickedOnEngine] = useState(false)
    const [currentStep, setCurrentStep] = useState(startStep)
    const [selectorOptions, setSelectorOptions] = useState({
      type: [],
      year: [],
      make: [],
      model: [],
      subModel: [],
      engine: [],
    })
    const { clearAnswers: clearSpecificConditionAnswers } =
      useSpecificConditionStore()

    const getCurrentStep = useCallback((): VehicleSelectorStep => {
      if (currentSelections.engine?.value) return VehicleSelectorStep.END
      if (currentSelections.subModel?.value) return VehicleSelectorStep.ENGINE
      if (currentSelections.model?.value)
        return StoreInstances.userStore.isCclEnabled
          ? VehicleSelectorStep.SUBMODEL
          : VehicleSelectorStep.ENGINE
      if (currentSelections.make?.value) return VehicleSelectorStep.MODEL
      if (currentSelections.year?.value) return VehicleSelectorStep.MAKE
      if (currentSelections.type?.value) return VehicleSelectorStep.YEAR

      if (startStep) {
        return startStep
      }
      return VehicleSelectorStep.TYPE
    }, [currentSelections, startStep])

    const calculateCurrentStep = useCallback(() => {
      setCurrentStep(getCurrentStep())
    }, [getCurrentStep])

    const enabled = (step, value) => {
      if (step === VehicleSelectorStep.TYPE) {
        return true
      }
      const currentStepIdx = stepOrder.indexOf(currentStep)
      const compareIdx = stepOrder.indexOf(step)
      return currentStepIdx >= compareIdx || value
    }

    const setVehicleOption = useCallback(
      (id: string, option: VehicleDropdownOnChangeProps) => {
        const updatedSelections = { ...currentSelections }
        updatedSelections[id] = option.selection
        if (id === VehicleSelectorStep.TYPE) {
          updatedSelections.year = undefined
        }
        if (
          id === VehicleSelectorStep.TYPE ||
          id === VehicleSelectorStep.YEAR
        ) {
          updatedSelections.make = undefined
        }
        if (
          id === VehicleSelectorStep.TYPE ||
          id === VehicleSelectorStep.YEAR ||
          id === VehicleSelectorStep.MAKE
        ) {
          updatedSelections.model = undefined
        }
        if (
          StoreInstances.userStore.isCclEnabled &&
          (id === VehicleSelectorStep.TYPE ||
            id === VehicleSelectorStep.YEAR ||
            id === VehicleSelectorStep.MAKE ||
            id === VehicleSelectorStep.MODEL)
        ) {
          updatedSelections.subModel = undefined
        }

        if (
          id === VehicleSelectorStep.TYPE ||
          id === VehicleSelectorStep.YEAR ||
          id === VehicleSelectorStep.MAKE ||
          id === VehicleSelectorStep.MODEL ||
          id === VehicleSelectorStep.SUBMODEL
        ) {
          updatedSelections.engine = undefined
        }
        if (id === VehicleSelectorStep.MODEL) {
          updatedSelections.modelType = option.selectionType
        }
        setCurrentSelections(updatedSelections)
      },
      [currentSelections, setCurrentSelections]
    )

    const handleSelect = useCallback(
      (step, selection) => {
        setVehicleOption(step, selection)
        switch (step) {
          case VehicleSelectorStep.TYPE:
            setTimeout(() => {
              setCurrentStep(VehicleSelectorStep.YEAR)
            })
            break
          case VehicleSelectorStep.YEAR:
            setSelectorOptions({
              ...selectorOptions,
              make: [],
              model: [],
              subModel: [],
              engine: [],
            })
            setTimeout(() => {
              setCurrentStep(VehicleSelectorStep.MAKE)
            })
            break
          case VehicleSelectorStep.MAKE:
            setSelectorOptions({
              ...selectorOptions,
              model: [],
              subModel: [],
              engine: [],
            })
            setTimeout(() => {
              setCurrentStep(VehicleSelectorStep.MODEL)
            })
            break
          case VehicleSelectorStep.MODEL:
            setSelectorOptions({
              ...selectorOptions,
              subModel: [],
              engine: [],
            })
            if (StoreInstances.userStore.isCclEnabled) {
              const updatedSelections = { ...currentSelections }
              updatedSelections.model = selection.selection
              updatedSelections.modelType = selection.selectionType
              updatedSelections.subModel = undefined
              updatedSelections.engine = undefined
              VehicleServiceProvider.getSelectorOptions(
                VehicleSelectorStep.ENGINE,
                updatedSelections,
                filterMakes
              ).then((engineOptions) => {
                setSelectorOptions((prev) => ({
                  ...prev,
                  engine: engineOptions,
                }))
              })
            }
            setTimeout(() => {
              if (StoreInstances.userStore.isCclEnabled) {
                setCurrentStep(VehicleSelectorStep.SUBMODEL)
              } else {
                setCurrentStep(VehicleSelectorStep.ENGINE)
              }
            })

            break
          case VehicleSelectorStep.SUBMODEL: {
            setSelectorOptions({
              ...selectorOptions,
              engine: [],
            })
            setTimeout(() => {
              setCurrentStep(VehicleSelectorStep.ENGINE)
            })
            break
          }
          case VehicleSelectorStep.ENGINE:
            searchStore.setShowVehicleTransition(true)
            setCurrentStep(VehicleSelectorStep.END)
            setUserClickedOnEngine(true)
            break
          default:
            break
        }
      },
      [
        currentSelections,
        filterMakes,
        searchStore,
        selectorOptions,
        setVehicleOption,
      ]
    )

    const handleGo = useCallback(async () => {
      const cancelUpdateVehicle = await searchStore.updateCurrentVehicle({
        ...currentSelections,
        vin: null,
        plate: null,
      })
      if (cancelUpdateVehicle) {
        resetStore()
        StoreInstances.astPunchOutStore.catalog.resetStore()
        clearSpecificConditionAnswers()
        vehicleWidgetStore.setIsDrawerOpened(false)
        vehicleWidgetStore.setErrorState(VehicleWidgetErrorState.none)
        vehicleWidgetStore.setDisplayState(VehicleWidgetDisplayState.view)
        onVehicleSelected()
      }
    }, [
      clearSpecificConditionAnswers,
      searchStore,
      currentSelections,
      onVehicleSelected,
      resetStore,
      vehicleWidgetStore,
    ])

    useEffect(() => {
      if (
        currentSelections.engine?.id >= 0 &&
        currentStep === VehicleSelectorStep.END &&
        userClickedOnEngine
      ) {
        setUserClickedOnEngine(false)
        handleGo()
        if (editing) {
          clearStore()
          navigate('/partsCatalog')
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSelections, currentStep, editing, handleGo, userClickedOnEngine])

    const handleClearSelections = useCallback(() => {
      setCurrentSelections({
        ...currentSelections,
        year: undefined,
        make: undefined,
        model: undefined,
        subModel: undefined,
        engine: undefined,
      })
    }, [currentSelections, setCurrentSelections])

    useEffect(() => {
      //MPV3-9251: Add debouncing to prevent continuous API calls when changing vehicle selections
      const timeoutId = setTimeout(() => {
        if (!isMounted()) return

        makesStore.setIsLoadingMakes(true)
        VehicleServiceProvider.getSelectorOptions(
          currentStep,
          currentSelections || {},
          filterMakes
        )
          .then((options) => {
            if (!isMounted()) {
              return
            }
            setSelectorOptions((prevState) => ({
              ...prevState,
              [currentStep]: options,
            }))

            if (
              StoreInstances.userStore.isCclEnabled &&
              currentStep === VehicleSelectorStep.SUBMODEL
            ) {
              // Validate if option response is "No Sub Models Available"
              const hasNoSubModels = options.some((item) =>
                item.options?.some(
                  (option) => option.value === 'No Sub Models Available'
                )
              )
              if (hasNoSubModels) {
                setCurrentStep(VehicleSelectorStep.ENGINE)
              } else if (options[0]?.options?.length === 1) {
                // Auto-select if only one submodel available
                const singleSubModel = options[0].options[0]
                handleSelect(VehicleSelectorStep.SUBMODEL, {
                  selection: singleSubModel,
                })
              }
            }
          })
          .finally(() => makesStore.setIsLoadingMakes(false))
      }, 300)

      return () => clearTimeout(timeoutId)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep, isMounted, filterMakes, customMakesKey])

    useEffect(calculateCurrentStep, [calculateCurrentStep])

    // eslint-disable-next-line eqeqeq
    const searchDisabled = currentSelections.engine?.id == null

    useEffect(() => {
      if (Object.keys(currentVehicle).length !== 0) {
        setCurrentSelections({
          type: currentVehicle.type,
          year: currentVehicle.year,
          make: currentVehicle.make,
          model: currentVehicle.model,
          subModel: currentVehicle.subModel,
          engine: currentVehicle.engine,
          modelType: currentVehicle.modelType,
        })
      }
    }, [currentVehicle, setCurrentSelections])

    const setVehicleTypeOptions = useCallback(async () => {
      const { getVehicleTypePreference, setVehicleTypes } =
        StoreInstances.customizeSearchStore

      const defaultType = getVehicleTypePreference()

      const types = await UserDataServiceProvider.getVehicleCatalogs()

      const sortedTypes = sortVehicleTypes(types, defaultType)

      setVehicleTypes(sortedTypes)

      setSelectorOptions((prev) => ({
        ...prev,
        type: VehicleServiceProvider.getVehicleTypes(),
      }))

      handleSelect(VehicleSelectorStep.TYPE, {
        selection: sortedTypes?.[0],
      })

      setIsYearAutoOpened(true)
    }, [handleSelect])

    useEffect(() => {
      if (currentSelections?.type === undefined) {
        setVehicleTypeOptions()
      }
    }, [currentSelections?.type, setVehicleTypeOptions])

    useEffect(() => {
      const { getSelectedMakes } = VehicleServiceProvider
      const setupToggle = async (): Promise<void> => {
        const enabled = await VehicleServiceProvider.isMakesFilterEnabled()
        makesStore.setMakesFilterEnabled(enabled)
        const selectedMakes = await getSelectedMakes()
        makesStore.setSelectedMakes(selectedMakes)
        makesStore.setShowMakesToggle(selectedMakes?.length > 0)
      }
      setupToggle()
    }, [makesStore])

    return (
      <>
        <FirstRow>
          <AddVehicleControls>
            <VehicleDropdowns>
              <VehicleType>
                <VehicleDropdown
                  label="vehicleType"
                  selection={
                    currentSelections.type
                      ? {
                          ...currentSelections.type,
                          value: Translate2(currentSelections.type.value),
                        }
                      : undefined
                  }
                  active={currentStep === VehicleSelectorStep.TYPE}
                  disabled={false}
                  onClick={() => setCurrentStep(VehicleSelectorStep.TYPE)}
                  onChange={(selection) =>
                    handleSelect(VehicleSelectorStep.TYPE, selection)
                  }
                  options={selectorOptions.type}
                  enableSearch
                />
              </VehicleType>
              <Year>
                <VehicleDropdown
                  label="year"
                  selection={currentSelections.year}
                  active={
                    currentStep === VehicleSelectorStep.YEAR &&
                    !isYearAutoOpened
                  }
                  disabled={
                    !enabled(VehicleSelectorStep.YEAR, currentSelections.year)
                  }
                  onClick={() => {
                    setIsYearAutoOpened(false)
                    setCurrentStep(VehicleSelectorStep.YEAR)
                  }}
                  onChange={(selection) => {
                    setIsYearAutoOpened(false)
                    handleSelect(VehicleSelectorStep.YEAR, selection)
                  }}
                  options={selectorOptions.year}
                  enableSearch
                  disableSearch={currentSelections.type === undefined}
                />
              </Year>
              <Make $isCclEnable={StoreInstances.userStore.isCclEnabled}>
                <VehicleDropdown
                  label="make"
                  selection={currentSelections.make}
                  active={
                    currentStep === VehicleSelectorStep.MAKE &&
                    !isYearAutoOpened
                  }
                  disabled={
                    !enabled(VehicleSelectorStep.MAKE, currentSelections.make)
                  }
                  onClick={() => {
                    setIsYearAutoOpened(false)
                    setCurrentStep(VehicleSelectorStep.MAKE)
                  }}
                  onChange={(selection) => {
                    setIsYearAutoOpened(false)
                    handleSelect(VehicleSelectorStep.MAKE, selection)
                  }}
                  options={selectorOptions.make}
                  enableSearch
                  disableSearch={currentSelections.year === undefined}
                />
              </Make>
              <Model $isCclEnable={StoreInstances.userStore.isCclEnabled}>
                <VehicleDropdown
                  label="model"
                  selection={currentSelections.model}
                  active={
                    currentStep === VehicleSelectorStep.MODEL &&
                    !isYearAutoOpened
                  }
                  disabled={
                    !enabled(VehicleSelectorStep.MODEL, currentSelections.model)
                  }
                  onClick={() => {
                    setIsYearAutoOpened(false)
                    setCurrentStep(VehicleSelectorStep.MODEL)
                  }}
                  onChange={(selection) => {
                    setIsYearAutoOpened(false)
                    handleSelect(VehicleSelectorStep.MODEL, selection)
                  }}
                  options={selectorOptions.model}
                  enableSearch
                  disableSearch={currentSelections.make === undefined}
                />
              </Model>
              {/* Use the actual attribute once backend is ready. */}
              {StoreInstances.userStore.isCclEnabled && (
                <SubModel>
                  <VehicleDropdown
                    label="subModelOptional"
                    selection={currentSelections.subModel}
                    active={
                      currentStep === VehicleSelectorStep.SUBMODEL &&
                      !isYearAutoOpened
                    }
                    disabled={
                      !enabled(
                        VehicleSelectorStep.SUBMODEL,
                        currentSelections.subModel
                      )
                    }
                    onClick={() => {
                      setIsYearAutoOpened(false)
                      setCurrentStep(VehicleSelectorStep.SUBMODEL)
                    }}
                    onChange={(selection) => {
                      setIsYearAutoOpened(false)
                      handleSelect(VehicleSelectorStep.SUBMODEL, selection)
                    }}
                    options={selectorOptions.subModel}
                    enableSearch
                    disableSearch={currentSelections.model === undefined}
                  />
                </SubModel>
              )}
              <Engine $isCclEnable={StoreInstances.userStore.isCclEnabled}>
                <VehicleDropdown
                  label="engine"
                  selection={currentSelections.engine}
                  active={
                    currentStep === VehicleSelectorStep.ENGINE &&
                    !isYearAutoOpened
                  }
                  disabled={
                    !enabled(
                      VehicleSelectorStep.ENGINE,
                      currentSelections.engine
                    )
                  }
                  onClick={() => {
                    setIsYearAutoOpened(false)
                    setCurrentStep(VehicleSelectorStep.ENGINE)
                    // Fetch fresh engine options based on current selections
                    // (subModel will be included only if user selected one)
                    VehicleServiceProvider.getSelectorOptions(
                      VehicleSelectorStep.ENGINE,
                      currentSelections,
                      filterMakes
                    ).then((engineOptions) => {
                      setSelectorOptions((prev) => ({
                        ...prev,
                        engine: engineOptions,
                      }))
                    })
                  }}
                  onChange={(selection) => {
                    setIsYearAutoOpened(false)
                    handleSelect(VehicleSelectorStep.ENGINE, selection)
                  }}
                  options={selectorOptions.engine}
                  enableSearch
                  disableSearch={currentSelections.model === undefined}
                />
              </Engine>
            </VehicleDropdowns>

            <FirstRowButtons>
              {!editing && (
                <AddButton
                  onClick={handleGo}
                  text="add"
                  size="large"
                  disabled={searchDisabled}
                  loading={false}
                />
              )}

              {!editing && (
                <SaveButton
                  onClick={handleGo}
                  text="add"
                  disabled={searchDisabled}
                  loading={false}
                />
              )}
            </FirstRowButtons>
          </AddVehicleControls>
        </FirstRow>
        {editing && (
          <EditControls
            onConfirm={handleGo}
            onCancel={onVehicleSelected}
            onClearSelections={handleClearSelections}
            updateDisabled={currentStep !== VehicleSelectorStep.END}
          />
        )}
      </>
    )
  }
)

VehicleSelector.displayName = 'VehicleSelector'

const FirstRow = styled.div`
  display: flex;
  flex-grow: 1;
  width: 100%;
`

const VehicleDropdowns = styled.div`
  display: flex;
  flex-grow: 1;
  gap: 8px;
  @media screen and (width < ${breakpoints.tabletLarge}px) {
    flex-direction: column;
    align-items: center;
    width: 100%;
  }
`

const AddVehicleControls = styled.div`
  display: flex;
  flex-grow: 1;
  gap: 8px;
  @media screen and (width < ${breakpoints.tabletLarge}px) {
    flex-direction: column;
    align-items: center;
    width: 100%;
  }
`

const FirstRowButtons = styled.div`
  display: flex;
  margin-left: auto;
`

const AddButton = styled(Button)`
  margin: auto 0px 0px 0px;

  @media screen and (width < ${breakpoints.tabletLarge}px) {
    display: none;
  }
`

const SaveButton = styled(Button)`
  margin-top: auto;
  display: none;
  @media screen and (width < ${breakpoints.tabletLarge}px) {
    display: block;
    margin: 0px 0px 10px 0px;
    width: 100%;
  }
`

const VehicleType = styled.div`
  width: 100%;
  @media screen and (width > ${breakpoints.laptopXLarge}px) {
    width: 332px;
  }

  @media screen and (${breakpoints.laptopMedium}px < width <= ${breakpoints.laptopXLarge}px) {
    min-width: 248px;
    max-width: 332px;
  }

  @media screen and (${breakpoints.laptop}px < width <= ${breakpoints.laptopMedium}px) {
    min-width: 175px;
    max-width: 248px;
  }
`

const Year = styled.div`
  width: 100%;
  @media screen and (width > ${breakpoints.laptopXLarge}px) {
    width: 166px;
  }

  @media screen and (${breakpoints.laptopMedium}px < width <= ${breakpoints.laptopXLarge}px) {
    min-width: 124px;
    max-width: 166px;
  }

  @media screen and (${breakpoints.laptop}px < width <= ${breakpoints.laptopMedium}px) {
    min-width: 100px;
    max-width: 124px;
  }

  @media screen and (${breakpoints.tabletLarge}px < width <= ${breakpoints.laptop}px) {
    min-width: 100px;
    max-width: 100px;
  }

  @media screen and (width < ${breakpoints.tabletLarge}px) {
    width: 100%;
  }
`

const Make = styled.div<{ $isCclEnable?: boolean }>`
  width: 100%;

  @media screen and (width > ${breakpoints.laptopXLarge}px) {
    width: ${({ $isCclEnable }) => ($isCclEnable ? '284px' : '284px')};
  }

  @media screen and (${breakpoints.laptopMedium}px < width <= ${breakpoints.laptopXLarge}px) {
    ${({ $isCclEnable }) =>
      $isCclEnable
        ? `
          min-width: 195px;
          max-width: 224px;
        `
        : `
          min-width: 195px;
          max-width: 284px;
        `}
  }

  @media screen and (${breakpoints.laptop}px < width <= ${breakpoints.laptopMedium}px) {
    min-width: 172px;
    max-width: 195px;
  }
`

const Model = styled.div<{ $isCclEnable?: boolean }>`
  width: 100%;

  @media screen and (width > ${breakpoints.laptopXLarge}px) {
    width: ${({ $isCclEnable }) => ($isCclEnable ? '284px' : '332px')};
  }

  @media screen and (${breakpoints.laptopMedium}px < width <= ${breakpoints.laptopXLarge}px) {
    ${({ $isCclEnable }) =>
      $isCclEnable
        ? `width: 224px;`
        : `
          min-width: 248px;
          max-width: 332px;
        `}
  }

  @media screen and (${breakpoints.laptop}px < width <= ${breakpoints.laptopMedium}px) {
    ${({ $isCclEnable }) =>
      $isCclEnable
        ? `width: 224px;`
        : `
          min-width: 175px;
          max-width: 248px;
        `}
  }
`

const SubModel = styled.div`
  width: 100%;
  @media screen and (width > ${breakpoints.laptopXLarge}px) {
    width: 284px;
  }

  @media screen and (${breakpoints.laptopMedium}px < width <= ${breakpoints.laptopXLarge}px) {
    width: 224px;
  }

  @media screen and (${breakpoints.laptop}px < width <= ${breakpoints.laptopMedium}px) {
    width: 224px;
  }
`

const Engine = styled.div<{ $isCclEnable?: boolean }>`
  width: 100%;

  @media screen and (width > ${breakpoints.laptopXLarge}px) {
    width: 332px;
  }

  @media screen and (${breakpoints.laptopMedium}px < width <= ${breakpoints.laptopXLarge}px) {
    ${({ $isCclEnable }) =>
      $isCclEnable
        ? `width: 224px;`
        : `
          min-width: 248px;
          max-width: 332px;
        `}
  }

  @media screen and (${breakpoints.laptop}px < width <= ${breakpoints.laptopMedium}px) {
    ${({ $isCclEnable }) =>
      $isCclEnable
        ? `width: 224px;`
        : `
          min-width: 175px;
          max-width: 248px;
        `}
  }
`
